import { useEffect, useRef, useState, type ReactNode } from 'react';
import { RefreshCw } from './icons';

const THRESHOLD = 72;

export interface PullToRefreshProps {
  onRefresh: () => void | Promise<void>;
  children: ReactNode;
}

/**
 * Touch pull-to-refresh for the job lists (momentum friendly):
 * drag down at the top of the page, release past the threshold to reload.
 * Honours prefers-reduced-motion via CSS (spinner stops, state still works).
 */
export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const pullRef = useRef(0);
  const busyRef = useRef(false);
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;

  useEffect(() => {
    let startY = 0;
    let active = false;

    const onStart = (e: TouchEvent) => {
      if (window.scrollY > 0 || busyRef.current) return;
      active = true;
      startY = e.touches[0]?.clientY ?? 0;
      pullRef.current = 0;
    };

    const onMove = (e: TouchEvent) => {
      if (!active) return;
      if (window.scrollY > 0) {
        active = false;
        pullRef.current = 0;
        setPull(0);
        return;
      }
      const y = e.touches[0]?.clientY ?? 0;
      const delta = y - startY;
      if (delta <= 0) {
        pullRef.current = 0;
        setPull(0);
        active = false;
        return;
      }
      if (e.cancelable) e.preventDefault();
      const next = Math.min(delta * 0.55, 112);
      pullRef.current = next;
      setPull(next);
    };

    const onEnd = () => {
      if (!active) return;
      active = false;
      if (pullRef.current < THRESHOLD) {
        pullRef.current = 0;
        setPull(0);
        return;
      }
      busyRef.current = true;
      setBusy(true);
      setPull(THRESHOLD);
      void Promise.resolve(refreshRef.current()).finally(() => {
        busyRef.current = false;
        setBusy(false);
        pullRef.current = 0;
        setPull(0);
      });
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  const progress = busy ? 1 : Math.min(pull / THRESHOLD, 1);
  const armed = pull >= THRESHOLD && !busy;
    const label = busy ? 'Refreshing…' : armed ? 'Release to refresh' : pull > 12 ? 'Pull to refresh' : '';

  return (
    <div className="ptr" data-testid="pull-to-refresh" data-state={busy ? 'busy' : armed ? 'armed' : pull > 12 ? 'pulling' : 'idle'}>
      <div className="ptr__rail" aria-hidden="true">
        <div className="ptr__indicator" style={{ opacity: Math.min(progress * 1.8, 1), transform: `translateY(${(progress - 1) * 10}px) scale(${0.75 + progress * 0.25})` }}>
          <RefreshCw size={16} className={busy ? 'ptr__spin' : ''} />
        </div>
        <span className="ptr__label">{label}</span>
      </div>
      <div className="ptr__body">{children}</div>
    </div>
  );
}
